// Create/Edit dialog for a Payment Intermediary.
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import type {
  PaymentIntermediary,
  PaymentIntermediaryStatus,
} from '@/types/payment-intermediary';
import {
  useCreatePaymentIntermediaryMutation,
  useUpdatePaymentIntermediaryMutation,
} from './api';

type Mode = 'create' | 'edit';

export interface PaymentIntermediaryFormDialogProps {
  mode: Mode;
  initial?: PaymentIntermediary | null;
  onClose: (saved: PaymentIntermediary | null) => void;
}

const schema = z.object({
  name: z.string().trim().min(1, { message: 'Required' }),
  accountNumber: z.string().trim().min(1, { message: 'Required' }),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

type FormValues = z.infer<typeof schema>;

export function PaymentIntermediaryFormDialog({
  mode,
  initial,
  onClose,
}: PaymentIntermediaryFormDialogProps) {
  const { t } = useTranslation();
  const createMutation = useCreatePaymentIntermediaryMutation();
  const updateMutation = useUpdatePaymentIntermediaryMutation();
  const isEdit = mode === 'edit';

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? '',
      accountNumber: initial?.accountNumber ?? '',
      status: (initial?.status as PaymentIntermediaryStatus) ?? 'ACTIVE',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      if (isEdit && initial) {
        const saved = await updateMutation.mutateAsync({
          id: initial.id,
          body: { name: values.name.trim(), accountNumber: values.accountNumber.trim() },
        });
        onClose(saved);
      } else {
        const saved = await createMutation.mutateAsync({
          name: values.name.trim(),
          accountNumber: values.accountNumber.trim(),
          status: values.status,
        });
        onClose(saved);
      }
    } catch (err) {
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        err instanceof Error ? err.message : String(err),
        'error',
      );
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? t('pages.paymentIntermediaries.dialog.editTitle', {
              defaultValue: 'Edit Payment Intermediary',
            })
          : t('pages.paymentIntermediaries.dialog.createTitle', {
              defaultValue: 'Create Payment Intermediary',
            })}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              {...register('name')}
              label={t('pages.paymentIntermediaries.fields.name', { defaultValue: 'Name' })}
              size="small"
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              {...register('accountNumber')}
              label={t('pages.paymentIntermediaries.fields.accountNumber', {
                defaultValue: 'Account Number',
              })}
              size="small"
              fullWidth
              required
              error={!!errors.accountNumber}
              helperText={errors.accountNumber?.message}
            />

            {/* Status — editable on create only; on edit it's managed via the toggle action */}
            {!isEdit ? (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.status}>
                    <InputLabel>
                      {t('pages.paymentIntermediaries.fields.status', { defaultValue: 'Status' })}
                    </InputLabel>
                    <Select
                      {...field}
                      label={t('pages.paymentIntermediaries.fields.status', {
                        defaultValue: 'Status',
                      })}
                    >
                      <MenuItem value="ACTIVE">
                        {t('pages.paymentIntermediaries.status.ACTIVE', {
                          defaultValue: 'Active',
                        })}
                      </MenuItem>
                      <MenuItem value="INACTIVE">
                        {t('pages.paymentIntermediaries.status.INACTIVE', {
                          defaultValue: 'Inactive',
                        })}
                      </MenuItem>
                    </Select>
                    {errors.status && (
                      <FormHelperText>{errors.status.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            ) : (
              <FormControlLabel
                control={<Switch checked={initial?.status === 'ACTIVE'} disabled />}
                label={
                  initial?.status === 'ACTIVE'
                    ? t('pages.paymentIntermediaries.status.ACTIVE', { defaultValue: 'Active' })
                    : t('pages.paymentIntermediaries.status.INACTIVE', {
                        defaultValue: 'Inactive',
                      })
                }
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(null)} disabled={pending}>
            {t('global.actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="submit" variant="contained" disabled={pending}>
            {isEdit
              ? t('global.actions.save', { defaultValue: 'Save' })
              : t('global.actions.create', { defaultValue: 'Create' })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
